import { formatDate, formatTime } from '@/lib/formatDate';
import React, { useState } from 'react';
import {
  Eye, Edit, Printer, Paperclip, FileText, MoreHorizontal,
  ArrowUpDown, ArrowUp, ArrowDown, Plus, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PatientDocuments from '@/components/PatientDocuments/PatientDocuments';

import { cn } from '@/lib/utils';
import { Prescription } from '@/types/prescription';
import { displayAge } from '@/lib/patientAge';
import { useAuth } from '@/context/AuthContext';
import { canEditPrescription } from '@/lib/prescriptionEdit';

type SortField = 'patient' | 'date' | 'complaints' | 'medicines' | 'diagnosis';
type SortDirection = 'asc' | 'desc' | null;

interface PrescriptionDataTableProps {
  prescriptions: Prescription[];
  patients: any[];
  loading?: boolean;
  onView: (prescription: Prescription) => void;
  /** Absent without prescriptions:write */
  onEdit?: (prescription: Prescription) => void;
  onPrint: (prescription: Prescription) => void;

  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  statusFilter?: string;
  onStatusChange?: (value: string) => void;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (order: 'asc' | 'desc') => void;
  hideSearchAndFilters?: boolean;
  /** Filters on the page are hiding some prescriptions */
  filtered?: boolean;
  onCreate?: () => void;
}

// Helper function to check if date is today
const isToday = (date: string | Date) => {
  const today = new Date();
  const prescriptionDate = new Date(date);
  return today.toDateString() === prescriptionDate.toDateString();
};

export default function PrescriptionDataTable({
  prescriptions,
  patients,
  loading = false,
  onView,
  onEdit,
  onPrint,
  searchTerm = '',
  onSearchChange,
  statusFilter = 'all',
  onStatusChange,
  sortOrder = 'desc',
  onSortChange,
  hideSearchAndFilters = false,
  filtered = false,
  onCreate
}: PrescriptionDataTableProps) {
  const { currentUser } = useAuth();

  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [attachmentsFor, setAttachmentsFor] = useState<Prescription | null>(null);

  // Get patient details helper
  const getPatientDetails = (patientId: string) => {
    return patients.find(p => p.id === patientId) || {
      name: 'Unknown Patient',
      age: 0,
      gender: 'Unknown',
      phone: 'N/A'
    };
  };

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or reset
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField('date'); // Reset to default
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field || sortDirection === null) {
      return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-primary" />
      : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  // Sort prescriptions based on current sort settings
  const sortedPrescriptions = React.useMemo(() => {
    if (!sortField || sortDirection === null) {
      return prescriptions;
    }

    return [...prescriptions].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'patient':
          const patientA = getPatientDetails(a.patient_id);
          const patientB = getPatientDetails(b.patient_id);
          aVal = patientA.name.toLowerCase();
          bVal = patientB.name.toLowerCase();
          break;
        case 'date':
          aVal = new Date(a.date || a.created_at || '').getTime();
          bVal = new Date(b.date || b.created_at || '').getTime();
          break;
        case 'complaints':
          aVal = (a.complaints?.length || 0);
          bVal = (b.complaints?.length || 0);
          break;
        case 'medicines':
          aVal = (a.medicines?.length || 0);
          bVal = (b.medicines?.length || 0);
          break;
        case 'diagnosis':
          aVal = (a.diagnosis || '').toLowerCase();
          bVal = (b.diagnosis || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [prescriptions, sortField, sortDirection, patients]);

  // Handle row click to view prescription
  const handleRowClick = (prescription: Prescription, event: React.MouseEvent) => {
    // Don't trigger if clicking on buttons or checkboxes
    if ((event.target as HTMLElement).closest('button, input, a')) {
      return;
    }
    onView(prescription);
  };

  // Handle keyboard navigation
  const handleRowKeyDown = (prescription: Prescription, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onView(prescription);
    }
  };

  // Quick action handlers
  const handleQuickEdit = (prescription: Prescription, event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit?.(prescription);
  };

  const handleQuickPrint = (prescription: Prescription, event: React.MouseEvent) => {
    event.stopPropagation();
    onPrint(prescription);
  };

  const names = (list: any[] | undefined, pick: (x: any) => string | undefined) =>
    (list ?? []).map(pick).filter((s): s is string => !!s && !!s.trim());
  const complaintNames = (p: Prescription) => names(p.complaints as any[], c => (typeof c === 'string' ? c : c?.complaint));
  const medicineNames = (p: Prescription) => names(p.medicines as any[], m => m?.product || m?.brand_name);
  const patientName = (p: Prescription) => getPatientDetails(p.patient_id)?.name || p.patient_name || 'Unknown';

  // Sorted by date: a divider where the day changes (Today, Yesterday, then dates)
  const rxDay = (p: Prescription) => new Date(p.date || p.created_at || '').toDateString();
  const grouped = sortField === 'date' && !!sortDirection;
  const newDay = (i: number) => grouped
    &&(i === 0 || rxDay(sortedPrescriptions[i - 1]) !== rxDay(sortedPrescriptions[i]));
  const dayLabel = (p: Prescription) => {
    const d = new Date(p.date || p.created_at || '');
    const yday = new Date(); yday.setDate(yday.getDate() - 1);
    return isToday(d) ? 'Today' : d.toDateString() === yday.toDateString() ? 'Yesterday' : formatDate(d);
  };
  const dividerClass = "h-7 px-4 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground";

  // Sortable column heading — the arrow says which way, and only the active column shows one in colour
  const SortHead = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
    <th scope="col" className={cn('px-3 text-left font-medium whitespace-nowrap', className)}
        aria-sort={sortField === field && sortDirection ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => handleSort(field)} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground">
        {label}
        {getSortIcon(field)}
      </button>
    </th>
  );

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card overflow-hidden" aria-busy="true" aria-label="Loading prescriptions">
        <div className="h-8 border-b border-border bg-muted/40" />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex items-center h-10 px-4 border-b border-divider last:border-0">
            <div className="h-3.5 w-full rounded bg-muted animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {prescriptions.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FileText className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-semibold text-foreground">{filtered ? 'No prescriptions match' : 'No prescriptions'}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchTerm || statusFilter !== 'all' || filtered
                ? 'Nothing matches these filters.'
                : 'Prescriptions you write will appear here.'}
            </p>
            {onCreate && !searchTerm && statusFilter === 'all' && !filtered && (
              <Button variant="outline" size="sm" onClick={onCreate} className="mt-4 gap-1.5">
                <Plus className="w-4 h-4" /> New prescription
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Below 1280px (phones, tablets): one row per prescription — the full table only fits from xl. Tap opens it; Print stays one tap away */}
            <ul className="xl:hidden divide-y divide-border">
              {sortedPrescriptions.map((prescription, i) => {
                const prescriptionDate = new Date(prescription.date || prescription.created_at || '');
                const meds = medicineNames(prescription);
                // Quick OPD prescriptions often have complaints but no diagnosis
                const summary = prescription.diagnosis || complaintNames(prescription).join(', ');
                return (
                  <React.Fragment key={prescription.id}>
                  {newDay(i) && <li aria-hidden className={cn(dividerClass, "flex items-center")}>{dayLabel(prescription)}</li>}
                  <li>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(event) => handleRowClick(prescription, event as any)}
                      onKeyDown={(event) => handleRowKeyDown(prescription, event as any)}
                      className="flex items-start gap-3 px-4 py-3 active:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground truncate capitalize">{patientName(prescription)}</span>
                          <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            {/* Under a day divider the time is enough */}
                            {grouped && prescription.created_at ? formatTime(new Date(prescription.created_at))
                              : isToday(prescriptionDate) ? 'Today' : formatDate(prescriptionDate)}
                          </span>
                        </div>
                        {summary && <p className="text-sm text-foreground/80 truncate">{summary}</p>}
                        {meds.length > 0 && (
                          <p className="text-xs text-muted-foreground truncate">
                            {meds.length} medicine{meds.length === 1 ? '' : 's'} · {meds.join(', ')}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        className="h-8 w-8 p-0 flex-shrink-0 text-muted-foreground"
                        onClick={(event) => handleQuickPrint(prescription, event)}
                        aria-label={`Print prescription for ${patientName(prescription)}`}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      {onEdit && canEditPrescription(prescription, currentUser) && (
                        <Button
                          variant="ghost" size="sm"
                          className="h-8 w-8 p-0 flex-shrink-0 text-muted-foreground"
                          onClick={(event) => handleQuickEdit(prescription, event)}
                          aria-label={`Edit prescription for ${patientName(prescription)}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </li>
                  </React.Fragment>
                );
              })}
            </ul>

            {/* Table — from xl */}
            <div className="hidden xl:block">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  {/* Medicines get the remaining width — they are the longest lists */}
                  <col className="w-[18%]" />
                  <col className="w-[11rem]" />
                  <col className="w-[18%]" />
                  <col />
                  <col className="w-[16%]" />
                  <col className="w-[7.5rem]" />
                </colgroup>
                <thead>
                  <tr className="h-8 border-b border-border bg-muted/40 text-2xs text-muted-foreground">
                    <SortHead field="patient" label="Patient" className="pl-4" />
                    <SortHead field="date" label="Date" />
                    <SortHead field="complaints" label="Complaints" />
                    <SortHead field="medicines" label="Medicines" />
                    <SortHead field="diagnosis" label="Diagnosis" />
                    <th scope="col" className="pl-3 pr-4 text-right font-medium uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPrescriptions.map((prescription, i) => {
                    const patient = getPatientDetails(prescription.patient_id);
                    const prescriptionDate = new Date(prescription.date || prescription.created_at || '');
                    // `date` is date-only (UTC midnight), so the clock time comes from when the Rx was written
                    const writtenAt = prescription.created_at ? new Date(prescription.created_at) : null;
                    const complaints = complaintNames(prescription);
                    const meds = medicineNames(prescription);
                    const demographics = [
                      (patient?.age || patient?.date_of_birth) ? displayAge(patient?.age, patient?.date_of_birth) : null,
                      patient?.gender && patient.gender !== 'Unknown' ? patient.gender : null,
                    ].filter(Boolean).join(' · ');

                    return (
                      <React.Fragment key={prescription.id}>
                      {newDay(i) && (
                        <tr aria-hidden className={dividerClass}>
                          <td colSpan={6}>{dayLabel(prescription)}</td>
                        </tr>
                      )}
                      <tr
                        className="h-10 border-b border-divider last:border-0 cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60"
                        onClick={(event) => handleRowClick(prescription, event)}
                        onKeyDown={(event) => handleRowKeyDown(prescription, event)}
                        tabIndex={0}
                        aria-label={`Prescription for ${patientName(prescription)}, ${formatDate(prescriptionDate)}`}
                      >
                        <td className="pl-4 pr-3">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className="truncate font-medium text-foreground capitalize">{patientName(prescription)}</span>
                            {demographics && <span className="flex-shrink-0 text-xs text-muted-foreground">{demographics}</span>}
                          </div>
                        </td>
                        <td className="px-3 whitespace-nowrap tabular-nums">
                          <span className="text-foreground">{isToday(prescriptionDate) ? 'Today' : formatDate(prescriptionDate)}</span>
                          {writtenAt && <span className="ml-1.5 text-xs text-muted-foreground">{formatTime(writtenAt)}</span>}
                        </td>
                        <td className="px-3">
                          {complaints.length
                            ? <span className="block truncate text-foreground" title={complaints.join(', ')}>{complaints.join(', ')}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3">
                          {meds.length ? (
                            <span className="flex items-baseline gap-1.5 min-w-0" title={meds.join(', ')}>
                              <span className="flex-shrink-0 font-mono text-xs text-muted-foreground tabular-nums">{meds.length}</span>
                              <span className="truncate text-foreground">{meds.join(', ')}</span>
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3">
                          {prescription.diagnosis
                            ? <span className="block truncate text-foreground" title={prescription.diagnosis}>{prescription.diagnosis}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="pl-3 pr-4">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              onClick={(event) => handleQuickPrint(prescription, event)}
                              title="Print" aria-label={`Print prescription for ${patientName(prescription)}`}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {onEdit && canEditPrescription(prescription, currentUser) && (
                              <Button
                                variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                onClick={(event) => handleQuickEdit(prescription, event)}
                                title="Edit" aria-label={`Edit prescription for ${patientName(prescription)}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" aria-label={`More actions for ${patientName(prescription)}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onSelect={() => onView(prescription)}>
                                  <Eye className="w-4 h-4 mr-2" /> View
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setAttachmentsFor(prescription)}>
                                  <Paperclip className="w-4 h-4 mr-2" /> Attachments
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </>
        )}
      </div>

      <Dialog open={!!attachmentsFor} onOpenChange={(open) => !open && setAttachmentsFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attachments</DialogTitle>
          </DialogHeader>
          {attachmentsFor && (
            <PatientDocuments
              patientId={attachmentsFor.patient_id}
              link={{ prescriptionId: attachmentsFor.id }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
